import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toasts } from './components/Toasts';
import { ControlsPage } from './features/controls/ControlsPage';
import { KafkaPage } from './features/kafka/KafkaPage';
import { LedgerPage } from './features/ledger/LedgerPage';
import { OpsPage } from './features/ops/OpsPage';
import { PipelinePage } from './features/pipeline/PipelinePage';
import { WalletPage } from './features/wallet/WalletPage';
import { useAuth } from './hooks/useAuth';

/** Everything but the wallet's own landing view needs a signed-in user. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { meId } = useAuth();
  return meId ? <>{children}</> : <Navigate to="/" replace />;
}

const TITLES: Record<string, string> = {
  '/': 'Wallet',
  '/ledger': 'Ledger',
  '/controls': 'Controls',
  '/ops': 'Review queue',
  '/pipeline': 'Event monitor',
  '/kafka': 'Kafka control room',
};

/** One document, several pages - so the tab title has to be set by hand. */
function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = `${TITLES[pathname] ?? 'Wallet'} · LedgerFlow`;
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <>
      <DocumentTitle />
      <Routes>
        <Route path="/" element={<WalletPage />} />
        <Route
          path="/ledger"
          element={
            <RequireAuth>
              <LedgerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/controls"
          element={
            <RequireAuth>
              <ControlsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/ops"
          element={
            <RequireAuth>
              <OpsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/pipeline"
          element={
            <RequireAuth>
              <PipelinePage />
            </RequireAuth>
          }
        />
        <Route
          path="/kafka"
          element={
            <RequireAuth>
              <KafkaPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toasts />
    </>
  );
}
